/* DAILE v2 (dv) — photo customizer compositor. Exposes window.DVCZ.
 *
 * Browser port of "Customizer Assets/developer/renderer.js" (package schema 3,
 * layout revision 2026-09-22). Same composition, same maths:
 *   1. scene.base on the whole frame;
 *   2. every photo slot on its own layer: cover crop (focus / focusBySlot),
 *      homography approximated by a segments x segments mesh (2 affine
 *      triangles per cell, 0.75 px overlap; 12 x 12 by default, the reference
 *      uses 40 x 40 — see drawProjective), clip to the outer quad,
 *      optional paper lighting (linear RGB, before the mask), luminance mask;
 *   3. text_slots with a style: word wrap + binary-search shrink in a FLAT
 *      1600 px wide texture, then projected with fit "stretch", paper lighting;
 *   4. design_layers (logos): the whole PNG, fit "stretch" onto its quad,
 *      paper lighting when lighting === "paper";
 *   5. overlays in manifest order (relief, seams, dimensions), source-over.
 *   A selected layout replaces slots + text_slots + design_layers together.
 *
 * What differs from the reference (speed and memory only):
 *   - layers are storefront WebP files of manifest.asset_size (1600 px) made by
 *     redesign-docs/tools/build-cz-assets.py, plus a small set of
 *     asset_size_small (800 px: base_small, mask_small, path_small) used for
 *     outputs up to that width (phones); coordinates stay in canvas_size
 *     (3919 px) space and scale = output width / canvas_size[0];
 *   - each scene's base, merged overlays and masks are scaled to the output
 *     size ONCE and kept in an LRU cache (maxScenes, 3 on small screens);
 *     source-over is associative, so pre-merging the overlays is exact;
 *   - masks with manifest.mask_encoding "alpha" (white + alpha) are applied
 *     with one destination-in draw, no per-pixel pass;
 *   - lighting and masking run on the slot's bounding box, not the frame;
 *   - extras: zoom / zoomBySlot and cropBySlot (explicit crop rectangles),
 *     text overrides (font, weight, colour, size factor, align: left | center |
 *     right | justify; justify keeps the paragraph's last line centred; added
 *     2026-09-28 for the greeting card of dv-customizer), photo === null
 *     renders the empty scene, opts.signal aborts, opts.canvas reuses a canvas;
 *   - text direction (review fix 2026-09-28): a detached canvas has no page
 *     direction, so each paragraph takes the direction of its first strong
 *     letter, like dir="auto" on the customizer inputs and Pango in print.
 *     Arabic text is drawn right to left; justified lines place the words in
 *     visual (bidi) order.
 *
 * API (all coordinates in canvas_size pixels, quads TL TR BR BL):
 *   DVCZ.loadManifest(elOrSelectorOrObject) -> manifest   (also DVCZ.manifest)
 *   DVCZ.getScene(id[, manifest]) -> scene
 *   DVCZ.sceneFor({ set | product+pieces, orientation, kind }[, manifest]) -> scene
 *   DVCZ.layoutFor(scene, { orientation, logo }) -> layout id | undefined
 *   DVCZ.resolveLayout(scene, layoutId) -> scene with that layout's fields
 *   DVCZ.renderScene(sceneOrId, photo, opts) -> Promise<HTMLCanvasElement>
 *       opts: width (px, default min(1600, asset_size)), maxWidth (alias),
 *             segments (12), focus [x,y], focusBySlot {slotId:[x,y]},
 *             zoom (>=1), zoomBySlot {slotId:z}, cropBySlot {slotId:[x,y,w,h] 0..1},
 *             layoutId, message (string; undefined => style sample_text,
 *             '' => no text), text { font_family, font_weight,
 *             fallback_font_family, color, size_factor, size_max_factor },
 *             canvas, signal, manifest
 *       result canvas.dvcz = { scene, layout, text: { slotId: { fontPx, lines } } }
 *   DVCZ.preload(sceneOrId, opts) -> Promise (warms the layer cache at opts.width)
 *   DVCZ.prefetch(sceneOrId, { width }) -> Promise (downloads the files only,
 *       small set when width <= asset_size_small; for hover warm-ups)
 *   DVCZ.preparePhoto(File|Blob|url|image, maxSide = 2400) -> Promise<canvas>
 *   DVCZ.fitText(textSlot, message, textOpts) -> { ok, fontPx, lines }
 *   DVCZ.drawGuides(canvas, resolvedScene)
 *   DVCZ.clearCache(), DVCZ.setCacheLimit(n)
 *   Low level (reference names): homography, project, cropCover, drawProjective,
 *   luminanceMask, applyPaperLighting, messageTexture, loadImage.
 */
(() => {
  'use strict';

  if (window.DVCZ && window.DVCZ.version) return;

  const finite = Number.isFinite;
  const ratioOf = (aspect) => (typeof aspect === 'number' ? aspect : aspect[0] / aspect[1]);
  const clamp01 = (v) => Math.max(0, Math.min(1, v));

  const abortError = () => {
    const error = new Error('DVCZ render aborted');
    error.name = 'AbortError';
    return error;
  };
  const checkAbort = (signal) => {
    if (signal && signal.aborted) throw abortError();
  };

  /* ----------------------------------------------------------- geometry */

  function assertQuad(quad) {
    if (!Array.isArray(quad) || quad.length !== 4 || !quad.every((p) => p.length === 2 && p.every(finite))) {
      throw new Error('DVCZ: quad needs four [x,y] points: TL, TR, BR, BL.');
    }
    const turns = quad.map((p, i) => {
      const q = quad[(i + 1) % 4];
      const r = quad[(i + 2) % 4];
      return (q[0] - p[0]) * (r[1] - q[1]) - (q[1] - p[1]) * (r[0] - q[0]);
    });
    if (turns.some((v) => Math.abs(v) < 1e-8) || !(turns.every((v) => v > 0) || turns.every((v) => v < 0))) {
      throw new Error('DVCZ: quad must be convex, without self-intersection or degenerate corners.');
    }
  }

  /** Homography: unit square (u,v) -> destination quad. */
  function homography(quad) {
    assertQuad(quad);
    const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = quad;
    const dx1 = x1 - x2, dx2 = x3 - x2, dx3 = x0 - x1 + x2 - x3;
    const dy1 = y1 - y2, dy2 = y3 - y2, dy3 = y0 - y1 + y2 - y3;
    let g = 0, h = 0;
    if (Math.abs(dx3) + Math.abs(dy3) > 1e-9) {
      const den = dx1 * dy2 - dx2 * dy1;
      if (Math.abs(den) < 1e-12) throw new Error('DVCZ: cannot build the perspective.');
      g = (dx3 * dy2 - dx2 * dy3) / den;
      h = (dx1 * dy3 - dx3 * dy1) / den;
    }
    const a = x1 - x0 + g * x1, b = x3 - x0 + h * x3, c = x0;
    const d = y1 - y0 + g * y1, e = y3 - y0 + h * y3, f = y0;
    return { a, b, c, d, e, f, g, h };
  }

  function project(H, u, v) {
    const k = H.g * u + H.h * v + 1;
    return [(H.a * u + H.b * v + H.c) / k, (H.d * u + H.e * v + H.f) / k];
  }

  /** Cover crop [sx, sy, sw, sh] of a width x height image for a flat aspect.
   *  zoom >= 1 narrows the window (extension; zoom 1 = reference). */
  function cropCover(width, height, aspect, focus = [0.5, 0.5], zoom = 1) {
    const ratio = ratioOf(aspect);
    if (!(width > 0 && height > 0 && finite(ratio) && ratio > 0)) throw new Error('DVCZ: bad photo size or aspect.');
    let w = width, h = height;
    if (width / height > ratio) w = height * ratio; else h = width / ratio;
    const z = finite(zoom) && zoom > 1 ? zoom : 1;
    w /= z; h /= z;
    return [(width - w) * clamp01(focus[0]), (height - h) * clamp01(focus[1]), w, h];
  }

  const dimensions = (image) => [
    image.naturalWidth || image.videoWidth || image.width,
    image.naturalHeight || image.videoHeight || image.height,
  ];

  function makeCanvas(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
  }

  function triangle(ctx, texture, src, dst) {
    const [p, q, r] = src, [P, Q, R] = dst;
    const den = p[0] * (q[1] - r[1]) + q[0] * (r[1] - p[1]) + r[0] * (p[1] - q[1]);
    if (Math.abs(den) < 1e-12) return;
    const solve = (k) => [
      (P[k] * (q[1] - r[1]) + Q[k] * (r[1] - p[1]) + R[k] * (p[1] - q[1])) / den,
      (P[k] * (r[0] - q[0]) + Q[k] * (p[0] - r[0]) + R[k] * (q[0] - p[0])) / den,
      (P[k] * (q[0] * r[1] - r[0] * q[1]) + Q[k] * (r[0] * p[1] - p[0] * r[1]) + R[k] * (p[0] * q[1] - q[0] * p[1])) / den,
    ];
    const [a, c, e] = solve(0), [b, d, f] = solve(1);
    // Slight overlap removes anti-aliasing cracks between adjacent triangles;
    // the outer quad is clipped separately, so the overlap cannot escape.
    const cx = (P[0] + Q[0] + R[0]) / 3, cy = (P[1] + Q[1] + R[1]) / 3;
    ctx.save();
    ctx.beginPath();
    for (let i = 0; i < 3; i++) {
      const x = dst[i][0], y = dst[i][1];
      const len = Math.hypot(x - cx, y - cy) || 1;
      const ex = x + (x - cx) * 0.75 / len, ey = y + (y - cy) * 0.75 / len;
      if (i) ctx.lineTo(ex, ey); else ctx.moveTo(ex, ey);
    }
    ctx.closePath();
    ctx.clip();
    ctx.setTransform(a, b, c, d, e, f);
    ctx.drawImage(texture, 0, 0);
    ctx.restore();
  }

  /** Perspective via a mesh of affine triangles. crop: optional explicit
   *  [sx, sy, sw, sh] in image pixels.
   *  Default 12 x 12 (288 triangles) instead of the reference 40 x 40 (3,200):
   *  each triangle costs a save / clip / drawImage / restore, and 40 x 40 made
   *  every preview a 0.5-0.75 s long task on a mid-range phone (review
   *  2026-09-28). Over every quad of the manifest the worst piecewise-affine
   *  error of 12 x 12 is 0.18 px at a 1400 px output (0.006 px on the flat
   *  scenes); the reference harness shows no visible change. */
  function drawProjective(ctx, image, quad, aspect, { focus = [0.5, 0.5], segments = 12, fit = 'cover', zoom = 1, crop = null } = {}) {
    const [width, height] = dimensions(image);
    const rect = crop || (fit === 'stretch' ? [0, 0, width, height] : cropCover(width, height, aspect, focus, zoom));
    const H = homography(quad);
    const largest = Math.max(...quad.map((p, i) => Math.hypot(p[0] - quad[(i + 1) % 4][0], p[1] - quad[(i + 1) % 4][1])));
    const ratio = ratioOf(aspect);
    const tw = Math.max(2, Math.ceil(Math.min(4096, largest * 1.6) * Math.min(1, ratio)));
    const th = Math.max(2, Math.ceil(tw / ratio));
    const texture = makeCanvas(tw, th);
    const tc = texture.getContext('2d');
    tc.imageSmoothingQuality = 'high';
    tc.drawImage(image, rect[0], rect[1], rect[2], rect[3], 0, 0, tw, th);
    ctx.save();
    ctx.beginPath();
    quad.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
    ctx.clip();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    const n = Math.max(2, Math.min(100, Math.floor(segments)));
    // Project the (n+1)^2 grid once instead of 4 corners per cell.
    const grid = new Array((n + 1) * (n + 1));
    for (let y = 0; y <= n; y++) for (let x = 0; x <= n; x++) grid[y * (n + 1) + x] = project(H, x / n, y / n);
    for (let y = 0; y < n; y++) {
      for (let x = 0; x < n; x++) {
        const u = x / n, v = y / n, U = (x + 1) / n, V = (y + 1) / n;
        const a = grid[y * (n + 1) + x], b = grid[y * (n + 1) + x + 1];
        const c = grid[(y + 1) * (n + 1) + x + 1], d = grid[(y + 1) * (n + 1) + x];
        triangle(ctx, texture, [[u * tw, v * th], [U * tw, v * th], [U * tw, V * th]], [a, b, c]);
        triangle(ctx, texture, [[u * tw, v * th], [U * tw, V * th], [u * tw, V * th]], [a, c, d]);
      }
    }
    ctx.restore();
  }

  /** Reference helper: a grayscale PNG mask -> white + alpha canvas. The
   *  storefront masks are already encoded that way (mask_encoding "alpha"). */
  function luminanceMask(image, width, height) {
    const canvas = makeCanvas(width, height);
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(image, 0, 0, width, height);
    const pixels = ctx.getImageData(0, 0, width, height);
    const p = pixels.data;
    for (let i = 0; i < p.length; i += 4) {
      p[i + 3] = Math.round((0.2126 * p[i] + 0.7152 * p[i + 1] + 0.0722 * p[i + 2]) * p[i + 3] / 255);
      p[i] = p[i + 1] = p[i + 2] = 255;
    }
    ctx.putImageData(pixels, 0, 0);
    return canvas;
  }

  /* ----------------------------------------------------------- lighting */

  const LINEAR = new Float32Array(256);
  const SRGB = new Uint8Array(65536);
  const PHOTO_TERM = new Float64Array(256);
  const BASE_TERM = new Float64Array(256);
  for (let i = 0; i < 256; i++) {
    const s = i / 255;
    LINEAR[i] = s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }
  for (let i = 0; i < 65536; i++) {
    const l = i / 65535;
    SRGB[i] = Math.round(255 * (l <= 0.0031308 ? 12.92 * l : 1.055 * l ** (1 / 2.4) - 0.055));
  }
  for (let i = 0; i < 256; i++) {
    PHOTO_TERM[i] = LINEAR[i] * 0.997 + 0.003;
    BASE_TERM[i] = 0.15 + 0.85 * LINEAR[i];
  }

  /** resultLinear = (photoLinear * 0.997 + 0.003) * (0.15 + 0.85 * baseLinear)
   *  layer: canvas; base: canvas of the whole frame; (ox, oy): position of the
   *  layer inside the frame (0,0 for full-frame layers, as in the reference). */
  function applyPaperLighting(layer, base, ox = 0, oy = 0) {
    const w = layer.width, h = layer.height;
    const ctx = layer.getContext('2d', { willReadFrequently: true });
    const pixels = ctx.getImageData(0, 0, w, h);
    const p = pixels.data;
    const b = base.getContext('2d', { willReadFrequently: true }).getImageData(ox, oy, w, h).data;
    for (let i = 0; i < p.length; i += 4) {
      if (!p[i + 3]) continue;
      p[i] = SRGB[Math.min(65535, Math.round(PHOTO_TERM[p[i]] * BASE_TERM[b[i]] * 65535))];
      p[i + 1] = SRGB[Math.min(65535, Math.round(PHOTO_TERM[p[i + 1]] * BASE_TERM[b[i + 1]] * 65535))];
      p[i + 2] = SRGB[Math.min(65535, Math.round(PHOTO_TERM[p[i + 2]] * BASE_TERM[b[i + 2]] * 65535))];
    }
    ctx.putImageData(pixels, 0, 0);
  }

  /* ----------------------------------------------------------- manifest */

  const state = { manifest: null, cacheLimit: 0 };

  function loadManifest(source) {
    let data = source;
    if (typeof source === 'string') data = document.querySelector(source);
    if (data && data.nodeType === 1) data = JSON.parse(data.textContent);
    if (!data || typeof data !== 'object' || !data.scenes) throw new Error('DVCZ: manifest not found.');
    const styles = data.text_styles || {};
    const expandTexts = (texts) => {
      if (!texts) return texts;
      const out = {};
      for (const key of Object.keys(texts)) {
        const t = texts[key];
        out[key] = typeof t.style === 'string' ? { ...t, style: styles[t.style] } : t;
      }
      return out;
    };
    for (const id of Object.keys(data.scenes)) {
      const scene = data.scenes[id];
      if (!scene.canvas_size) scene.canvas_size = data.canvas_size;
      if (!scene.asset_size) scene.asset_size = data.asset_size;
      if (!scene.asset_size_small && data.asset_size_small) scene.asset_size_small = data.asset_size_small;
      if (!scene.mask_encoding) scene.mask_encoding = data.mask_encoding || 'luminance';
      scene.text_slots = expandTexts(scene.text_slots);
      (scene.layouts || []).forEach((layout) => { layout.text_slots = expandTexts(layout.text_slots); });
    }
    state.manifest = data;
    return data;
  }

  function getScene(id, manifest = state.manifest) {
    if (id && typeof id === 'object') return id;
    const scene = manifest && manifest.scenes[id];
    if (!scene) throw new Error('DVCZ: unknown scene ' + id);
    return scene;
  }

  /** sceneFor({ set: 'mini300', orientation: 'vertical', kind: 'with_box' })
   *  kind: puzzle | with_box | lid | photo | photo_text */
  function sceneFor(query, manifest = state.manifest) {
    const q = { ...query };
    if (q.set) Object.assign(q, manifest.sets[q.set]);
    const product = manifest.products[q.product];
    if (!product) return null;
    let id;
    if (q.kind === 'lid') id = product.lid;
    else if (q.product === 'POSTCARD') id = (product[q.orientation || 'vertical'] || {})[q.kind || 'photo'];
    else id = ((product[String(q.pieces)] || {})[q.orientation] || {})[q.kind || 'puzzle'];
    return id ? manifest.scenes[id] : null;
  }

  /** Layout matching an orientation (and logo on/off when the scene has both). */
  function layoutFor(scene, { orientation, logo } = {}) {
    const layouts = scene.layouts || [];
    const match = layouts.filter((l) => (!orientation || l.orientation === orientation)
      && (logo === undefined || l.logo === Boolean(logo)));
    return (match[0] || layouts.find((l) => l.id === scene.default_layout) || {}).id;
  }

  /** A selected design replaces photo, text and logo fields atomically. */
  function resolveLayout(scene, layoutId = scene.default_layout) {
    if (!scene.layouts || !scene.layouts.length) return scene;
    const layout = scene.layouts.find((l) => l.id === (layoutId || scene.default_layout));
    if (!layout) throw new Error('DVCZ: unknown layout ' + layoutId);
    return {
      ...scene,
      slots: layout.slots,
      text_slots: layout.text_slots,
      design_layers: layout.design_layers,
      active_layout: layout.id,
    };
  }

  /* ----------------------------------------------------------- loading + cache */

  function loadImage(url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous'; // assets may come from the Shopify CDN origin
      image.decoding = 'async';
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('DVCZ: cannot load ' + url));
      image.src = url;
    });
  }

  const smallScreen = () => {
    try {
      return matchMedia('(max-width: 900px)').matches || (navigator.deviceMemory && navigator.deviceMemory <= 4);
    } catch (e) {
      return false;
    }
  };
  const cacheLimit = () => state.cacheLimit || (smallScreen() ? 3 : 6);

  const sceneCache = new Map(); // `${id}@${w}x${h}` -> Promise<entry>
  const designCache = new Map(); // url -> Promise<image>

  function touch(map, key, value, limit) {
    map.delete(key);
    map.set(key, value);
    while (map.size > limit) map.delete(map.keys().next().value);
    return value;
  }

  /** Scale a layer to the output size once (cached per scene and size).
   *  Tested against a JS area-average and a 2x-then-0.5x path: no fidelity
   *  gain in the harness (MAD within 0.05), so the plain draw is kept. */
  function scaled(image, width, height, read) {
    const canvas = makeCanvas(width, height);
    const ctx = canvas.getContext('2d', read ? { willReadFrequently: true } : undefined);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, 0, 0, width, height);
    return canvas;
  }

  /** Small layer set (asset_size_small, 800 px) for outputs up to that width:
   *  phones render at 800 px and used to download the 1600 px layers only to
   *  scale them down (review fix 2026-09-28). */
  const useSmall = (scene, width) => !!scene.base_small && width <= ((scene.asset_size_small || [0])[0] || 0);

  /** Files of a scene: base, overlays, masks (mask key = the large URL, as in the slots). */
  function sceneFiles(scene, small) {
    const masks = new Map();
    const collect = (slots) => (slots || []).forEach((s) => s.mask && masks.set(s.mask, (small && s.mask_small) || s.mask));
    collect(scene.slots);
    (scene.layouts || []).forEach((l) => collect(l.slots));
    return {
      base: (small && scene.base_small) || scene.base,
      overlays: (scene.overlays || []).map((o) => (small && o.path_small) || o.path),
      masks,
    };
  }

  /** base, merged overlays and every mask of the scene at the output size. */
  function sceneLayers(scene, width, height) {
    const key = scene.id + '@' + width + 'x' + height;
    if (sceneCache.has(key)) return touch(sceneCache, key, sceneCache.get(key), cacheLimit());
    const job = (async () => {
      const files = sceneFiles(scene, useSmall(scene, width));
      const masks = new Set(files.masks.keys());
      const [base, overlays, maskImages] = await Promise.all([
        loadImage(files.base),
        Promise.all(files.overlays.map((url) => loadImage(url))),
        Promise.all([...masks].map((m) => loadImage(files.masks.get(m)))),
      ]);
      const entry = { base: scaled(base, width, height, true), overlays: null, masks: new Map() };
      if (overlays.length) {
        entry.overlays = makeCanvas(width, height);
        const oc = entry.overlays.getContext('2d');
        oc.imageSmoothingEnabled = true;
        oc.imageSmoothingQuality = 'high';
        overlays.forEach((img) => oc.drawImage(img, 0, 0, width, height));
      }
      [...masks].forEach((url, i) => {
        entry.masks.set(url, scene.mask_encoding === 'alpha'
          ? scaled(maskImages[i], width, height)
          : luminanceMask(maskImages[i], width, height));
      });
      return entry;
    })();
    job.catch(() => sceneCache.delete(key));
    return touch(sceneCache, key, job, cacheLimit());
  }

  function designImage(url) {
    if (designCache.has(url)) return designCache.get(url);
    const job = loadImage(url);
    job.catch(() => designCache.delete(url));
    return touch(designCache, url, job, 4);
  }

  /* ----------------------------------------------------------- text */

  const fontSpec = (st, size) => {
    const weight = st.font_weight ? st.font_weight + ' ' : '';
    const font = st.font_family || 'Georgia';
    const fallback = st.fallback_font_family || 'Georgia';
    return `${weight}${size}px "${font}", "${fallback}", serif`;
  };

  // 'align' is an extension used by the greeting card (left | center | right |
  // justify); every package style is centred, so the default output is unchanged.
  const mergeStyle = (style, over) => {
    const st = { ...(style || {}) };
    if (over) {
      ['font_family', 'font_weight', 'fallback_font_family', 'color', 'size_factor', 'size_max_factor', 'align'].forEach((k) => {
        if (over[k] !== undefined) st[k] = over[k];
      });
    }
    return st;
  };

  /* Text direction (review fix 2026-09-28). The texture canvas is detached,
     so it never inherits the page's dir="rtl": the direction is decided per
     paragraph from its first strong letter (the rule of dir="auto" on the
     customizer inputs and of Pango in the print pipeline). Whole lines are
     drawn with that ctx.direction, so the browser's bidi algorithm orders
     mixed Arabic / Latin text; justified lines are placed word by word in
     visual order (visualOrder). */
  const RTL_LETTER = /[֐-ࣿיִ-﷿ﹰ-﻿]/;
  const LETTER = /\p{L}/u;
  const DIGIT = /\p{Nd}/u;
  /** 'rtl' | 'ltr' of the first strong letter, null when there is none. */
  function strongDir(text) {
    for (const ch of String(text)) {
      if (RTL_LETTER.test(ch)) return 'rtl';
      if (LETTER.test(ch)) return 'ltr';
    }
    return null;
  }
  // European digits read left to right; a word of punctuation only is neutral.
  const wordDir = (word) => strongDir(word) || (DIGIT.test(word) ? 'ltr' : null);

  /** Word-level bidi for one justified line: returns the word indices from
   *  left to right and each word's resolved direction. Runs of words in the
   *  other direction keep their own reading order (UAX #9 L2 at word level);
   *  neutral words take the direction around them, else the paragraph's. */
  function visualOrder(words, base) {
    const dirs = words.map(wordDir);
    const resolved = dirs.map((d, i) => {
      if (d) return d;
      let prev = null;
      let next = null;
      for (let j = i - 1; j >= 0 && !prev; j--) prev = dirs[j];
      for (let j = i + 1; j < dirs.length && !next; j++) next = dirs[j];
      return (prev || base) === (next || base) ? (prev || base) : base;
    });
    const runs = [];
    resolved.forEach((d, i) => {
      const run = runs[runs.length - 1];
      if (run && run.dir === d) run.items.push(i); else runs.push({ dir: d, items: [i] });
    });
    if (base === 'rtl') runs.reverse();
    const order = [];
    runs.forEach((run) => order.push(...(run.dir === 'rtl' ? run.items.slice().reverse() : run.items)));
    return { order, resolved };
  }

  /* ---- emoji (owner 22.10.2026): drawn from Google Noto Emoji (Apache 2.0 — may be printed on products; Apple /
     Samsung drawings may not) — the SAME pictures DAILE Work prints (daile-production lib/text.js v3.25), so the preview is
     the print. One SVG per emoji from jsDelivr (googlefonts/noto-emoji@v2.051). Metrics shared with Work: a square of
     EMOJI_SIZE em, advance EMOJI_ADV em, centred on the line's middle (textBaseline 'middle'). © ® ™ stay text. */
  const EMOJI_CDN = 'https://cdn.jsdelivr.net/gh/googlefonts/noto-emoji@v2.051/';
  const EMOJI_SIZE = 0.92, EMOJI_ADV = 1.08;
  const SEG = typeof Intl !== 'undefined' && Intl.Segmenter ? new Intl.Segmenter('en', { granularity: 'grapheme' }) : null;
  let EXT_PICT = null, REG_IND = null;
  try { EXT_PICT = new RegExp('\\p{Extended_Pictographic}', 'u'); REG_IND = new RegExp('\\p{Regional_Indicator}', 'u'); } catch (e) { /* very old engine: no emoji pictures */ }
  const graphemes = (text) => (SEG ? Array.from(SEG.segment(String(text || '')), (x) => x.segment) : Array.from(String(text || '')));
  function isEmoji(cl) {
    if (!EXT_PICT) return false;
    if (REG_IND.test(cl) || cl.indexOf('\u20E3') !== -1) return true;
    if (!EXT_PICT.test(cl)) return false;
    const cps = Array.from(cl, (c) => c.codePointAt(0));
    return !(cps.length === 1 && cps[0] < 0x2300);
  }
  const emojiName = (cl) => 'emoji_u' + Array.from(cl, (c) => c.codePointAt(0)).filter((cp) => cp !== 0xFE0F)
    .map((cp) => cp.toString(16).padStart(4, '0')).join('_');
  function splitEmoji(text) {
    const out = [];
    graphemes(text).forEach((cl) => {
      const t = isEmoji(cl) ? 'emoji' : 'text';
      const last = out[out.length - 1];
      if (t === 'text' && last && last.t === 'text') last.s += cl; else out.push({ t, s: cl });
    });
    return out;
  }
  const hasEmoji = (text) => EXT_PICT && splitEmoji(text).some((p) => p.t === 'emoji');
  const emojiImgs = new Map(); // name → { img, ok, wait }
  function loadEmoji(cl) {
    const name = emojiName(cl);
    if (emojiImgs.has(name)) return emojiImgs.get(name).wait;
    const rec = { img: null, ok: false, wait: null };
    rec.wait = (async () => {
      for (const dir of ['svg/', 'third_party/region-flags/waved-svg/']) {
        try {
          const r = await fetch(EMOJI_CDN + dir + name + '.svg', { mode: 'cors' });
          if (!r.ok) continue;
          // SVGs carry only a viewBox: give them a size, then load from a blob URL (same origin — the canvas stays clean)
          const svg = (await r.text()).replace(/<svg\b/, '<svg width="256" height="256"');
          const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
          const img = new Image();
          await new Promise((ok, no) => { img.onload = ok; img.onerror = no; img.src = url; });
          rec.img = img; rec.ok = true;
          return true;
        } catch (e) { /* next path / offline */ }
      }
      emojiImgs.delete(name); // retry next time (network may come back)
      return false;
    })();
    emojiImgs.set(name, rec);
    return rec.wait;
  }
  async function ensureEmoji(message) {
    if (!hasEmoji(message)) return;
    await Promise.all(splitEmoji(message).filter((p) => p.t === 'emoji').map((p) => loadEmoji(p.s)));
  }
  const emojiImage = (cl) => { const r = emojiImgs.get(emojiName(cl)); return r && r.ok ? r.img : null; };
  /** Width of a run at the current ctx.font (size px): text parts measured, emoji = EMOJI_ADV em. */
  function runWidth(ctx, text, size) {
    if (!hasEmoji(text)) return ctx.measureText(text).width;
    return splitEmoji(text).reduce((a, p) => a + (p.t === 'emoji' ? EMOJI_ADV * size : ctx.measureText(p.s).width), 0);
  }
  /** fillText with emoji pictures; align = left | right | center at x (textBaseline 'middle'). */
  function drawRun(ctx, text, x, y, align, size) {
    if (!hasEmoji(text)) { ctx.textAlign = align; ctx.fillText(text, x, y); return; }
    const parts = splitEmoji(text);
    const widths = parts.map((p) => (p.t === 'emoji' ? EMOJI_ADV * size : ctx.measureText(p.s).width));
    const total = widths.reduce((a, b) => a + b, 0);
    let cx = align === 'left' ? x : align === 'right' ? x - total : x - total / 2;
    const order = parts.map((p, i) => i);
    if (ctx.direction === 'rtl') order.reverse();
    ctx.textAlign = 'left';
    order.forEach((i) => {
      const p = parts[i];
      const img = p.t === 'emoji' ? emojiImage(p.s) : null;
      if (img) { const e = EMOJI_SIZE * size; ctx.drawImage(img, cx + (widths[i] - e) / 2, y - e / 2, e, e); } else ctx.fillText(p.s, cx, y);
      cx += widths[i];
    });
  }

  /** Flat layout: word wrap + binary search of the largest size that fits. */
  function layoutText(ctx, st, message, w, h) {
    const pad = st.padding_fraction ?? 0.055;
    const iw = w * (1 - 2 * pad), ih = h * (1 - 2 * pad), leading = st.line_height ?? 1.12;
    const maxLines = st.max_lines ?? 8;
    let curSize = 1;
    const setFont = (size) => { curSize = size; ctx.font = fontSpec(st, size); };
    const mw = (text) => runWidth(ctx, text, curSize);
    let ends = [];
    let dirs = [];
    const wrap = () => {
      const lines = [];
      ends = [];
      dirs = [];
      const push = (line, end, dir) => { lines.push(line); ends.push(end); dirs.push(dir); };
      for (const paragraph of String(message).split('\n')) {
        const dir = strongDir(paragraph) || 'ltr';
        let line = '';
        for (const word of paragraph.trim().split(/\s+/).filter(Boolean)) {
          if (mw(word) > iw) {
            if (line) { push(line, false, dir); line = ''; }
            for (const ch of graphemes(word)) { // emoji sequences (👨‍👩‍👧, flags) are never split
              if (mw(line + ch) > iw && line) { push(line, false, dir); line = ''; }
              line += ch;
            }
          } else if (line && mw(line + ' ' + word) > iw) {
            push(line, false, dir);
            line = word;
          } else {
            line += (line ? ' ' : '') + word;
          }
        }
        push(line, true, dir); // last line of a paragraph (never justified)
      }
      return lines;
    };
    let lo = 1;
    /* extension: size_max_factor > 1 raises both size caps of the style (the
       "Large" lid preset: x 1.25 in round 5, x 4/3 since dv-customizer 1.2.0).
       Shrink-to-fit still applies, so a long text stays in its field. */
    const capFactor = st.size_max_factor && st.size_max_factor > 1 ? st.size_max_factor : 1;
    let hi = Math.min(w * (st.font_size_max_width_fraction ?? 0.135), h * (st.font_size_max_height_fraction ?? 0.34)) * capFactor;
    for (let i = 0; i < 18; i++) {
      const mid = (lo + hi) / 2;
      setFont(mid);
      const lines = wrap();
      if (lines.length * mid * leading <= ih && lines.length <= maxLines) lo = mid; else hi = mid;
    }
    // extension: size_factor < 1 (the "Small" preset of the existing customizer)
    const factor = st.size_factor && st.size_factor > 0 && st.size_factor < 1 ? st.size_factor : 1;
    const size = lo * factor;
    setFont(size);
    const lines = wrap();
    const ok = lines.length * size * leading <= ih && lines.length <= maxLines;
    return { ok, size, lines, leading, ends: ends.slice(), dirs: dirs.slice(), innerWidth: iw };
  }

  /** Draws the wrapped lines with st.align (extension). 'center' is the
   *  reference behaviour; 'justify' spreads words over the inner width except
   *  on the last line of each paragraph, which is centred (like the existing
   *  customizer's text-align-last: center). left / right are physical sides.
   *  Every line is drawn in its paragraph's direction (see strongDir). */
  function drawLines(ctx, st, fit, w, top) {
    const align = st.align || 'center';
    const pad = (w - fit.innerWidth) / 2;
    const lineY = (i) => top + (i + 0.5) * fit.size * fit.leading;
    fit.lines.forEach((line, i) => {
      const y = lineY(i);
      const dir = (fit.dirs && fit.dirs[i]) || 'ltr';
      ctx.direction = dir;
      if (align === 'justify' && !fit.ends[i]) {
        const words = line.split(' ').filter(Boolean);
        if (words.length > 1) {
          const widths = words.map((word) => runWidth(ctx, word, fit.size));
          const gap = (fit.innerWidth - widths.reduce((a, b) => a + b, 0)) / (words.length - 1);
          const { order, resolved } = visualOrder(words, dir);
          ctx.textAlign = 'left';
          let x = pad;
          order.forEach((j) => {
            ctx.direction = resolved[j];
            drawRun(ctx, words[j], x, y, 'left', fit.size);
            x += widths[j] + gap;
          });
          return;
        }
      }
      if (align === 'left') { drawRun(ctx, line, pad, y, 'left', fit.size); return; }
      if (align === 'right') { drawRun(ctx, line, w - pad, y, 'right', fit.size); return; }
      drawRun(ctx, line, w / 2, y, 'center', fit.size);
    });
  }

  const textRatio = (slot) => {
    const ratio = ratioOf(slot.aspect || 0);
    if (!(ratio > 0)) throw new Error('DVCZ: text slot has no aspect.');
    return ratio;
  };

  function messageTexture(slot, message, over) {
    const st = mergeStyle(slot.style, over);
    const w = 1600, h = Math.max(1, Math.round(w / textRatio(slot)));
    const c = makeCanvas(w, h), ctx = c.getContext('2d');
    const fit = layoutText(ctx, st, message, w, h);
    if (!fit.ok) {
      const error = new Error('DVCZ: text is too long for this field.');
      error.code = 'DVCZ_TEXT_OVERFLOW';
      throw error;
    }
    ctx.fillStyle = st.color || '#AAA7A3';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const top = (h - fit.lines.length * fit.size * fit.leading) / 2;
    drawLines(ctx, st, fit, w, top);
    c.dvczFit = { fontPx: fit.size, lines: fit.lines, texWidth: w, texHeight: h };
    return c;
  }

  /** Validate a message before rendering (UI hint). fontPx is in the flat
   *  1600 px texture; compare between messages, not with CSS pixels. */
  function fitText(slot, message, over) {
    const st = mergeStyle(slot.style, over);
    const w = 1600, h = Math.max(1, Math.round(w / textRatio(slot)));
    const ctx = makeCanvas(1, 1).getContext('2d');
    const fit = layoutText(ctx, st, message, w, h);
    return { ok: fit.ok, fontPx: fit.size, lines: fit.lines, texWidth: w, texHeight: h };
  }

  async function ensureFont(st, message) {
    if (!document.fonts || !document.fonts.load) return;
    try {
      await document.fonts.load(fontSpec(st, 48), String(message || 'A'));
    } catch (e) { /* the fallback font is used */ }
  }

  /* ----------------------------------------------------------- render */

  /** Bounding box of a quad in output pixels, integer, 2 px margin, clamped. */
  function bbox(quad, width, height) {
    const xs = quad.map((p) => p[0]), ys = quad.map((p) => p[1]);
    const x0 = Math.max(0, Math.floor(Math.min(...xs)) - 2), y0 = Math.max(0, Math.floor(Math.min(...ys)) - 2);
    const x1 = Math.min(width, Math.ceil(Math.max(...xs)) + 2), y1 = Math.min(height, Math.ceil(Math.max(...ys)) + 2);
    return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
  }

  const isPaper = (lighting) => lighting === 'paper' || (lighting && lighting.type === 'paper');

  async function renderScene(sceneOrId, photo, opts = {}) {
    const manifest = opts.manifest || state.manifest;
    const original = getScene(sceneOrId, manifest);
    const scene = resolveLayout(original, opts.layoutId || original.default_layout);
    const size = scene.canvas_size || (manifest && manifest.canvas_size);
    if (!size || !size.every((n) => finite(n) && n > 0)) throw new Error('DVCZ: scene has no canvas_size.');
    const assetW = (scene.asset_size || [1600])[0];
    const requested = opts.width || opts.maxWidth || Math.min(1600, assetW);
    const scale = Math.min(1, requested / size[0]);
    const W = Math.round(size[0] * scale), Hh = Math.round(size[1] * scale);
    const segments = opts.segments || 12; // see drawProjective
    const signal = opts.signal;

    const layers = await sceneLayers(original, W, Hh);
    checkAbort(signal);

    const canvas = opts.canvas || makeCanvas(W, Hh);
    if (canvas.width !== W) canvas.width = W;
    if (canvas.height !== Hh) canvas.height = Hh;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, W, Hh);
    ctx.drawImage(layers.base, 0, 0);
    const info = { scene: scene.id, layout: scene.active_layout || null, text: {} };

    const toLocal = (quad, box) => quad.map(([x, y]) => [x * scale - box.x, y * scale - box.y]);

    // 2. photo slots
    if (photo) {
      for (const slot of scene.slots || []) {
        const quadOut = slot.quad.map(([x, y]) => [x * scale, y * scale]);
        const box = bbox(quadOut, W, Hh);
        const layer = makeCanvas(box.w, box.h);
        const lc = layer.getContext('2d', { willReadFrequently: true });
        const id = slot.id;
        const cropN = opts.cropBySlot && opts.cropBySlot[id];
        let crop = null;
        if (cropN) {
          const [pw, ph] = dimensions(photo);
          crop = [cropN[0] * pw, cropN[1] * ph, cropN[2] * pw, cropN[3] * ph];
        }
        drawProjective(lc, photo, toLocal(slot.quad, box), slot.aspect, {
          focus: (opts.focusBySlot && opts.focusBySlot[id]) || opts.focus || [0.5, 0.5],
          zoom: (opts.zoomBySlot && opts.zoomBySlot[id]) || opts.zoom || 1,
          crop,
          segments,
        });
        if (isPaper(slot.lighting)) applyPaperLighting(layer, layers.base, box.x, box.y);
        if (slot.mask) {
          const mask = layers.masks.get(slot.mask);
          lc.globalCompositeOperation = 'destination-in';
          lc.drawImage(mask, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
          lc.globalCompositeOperation = 'source-over';
        }
        ctx.drawImage(layer, box.x, box.y);
        checkAbort(signal);
      }
    }

    // 3. text (below seams and overlays)
    const texts = scene.text_slots || {};
    for (const key of Object.keys(texts)) {
      const slot = texts[key];
      if (!slot || !slot.style) continue;
      const value = opts.message ?? slot.sample_text ?? '';
      if (!String(value).trim()) continue;
      const st = mergeStyle(slot.style, opts.text);
      await ensureFont(st, value);
      await ensureEmoji(value);
      checkAbort(signal);
      const texture = messageTexture(slot, value, opts.text);
      info.text[key] = texture.dvczFit;
      const quadOut = slot.quad.map(([x, y]) => [x * scale, y * scale]);
      const box = bbox(quadOut, W, Hh);
      const layer = makeCanvas(box.w, box.h);
      drawProjective(layer.getContext('2d', { willReadFrequently: true }), texture, toLocal(slot.quad, box), slot.aspect, { segments, fit: 'stretch' });
      applyPaperLighting(layer, layers.base, box.x, box.y);
      ctx.drawImage(layer, box.x, box.y);
    }

    // 4. logos
    for (const design of scene.design_layers || []) {
      const image = await designImage(design.path);
      checkAbort(signal);
      const quadOut = design.quad.map(([x, y]) => [x * scale, y * scale]);
      const box = bbox(quadOut, W, Hh);
      const layer = makeCanvas(box.w, box.h);
      drawProjective(layer.getContext('2d', { willReadFrequently: true }), image, toLocal(design.quad, box), design.aspect, { segments, fit: 'stretch' });
      if (design.lighting === 'paper') applyPaperLighting(layer, layers.base, box.x, box.y);
      ctx.drawImage(layer, box.x, box.y);
    }

    // 5. overlays (pre-merged, same order)
    if (layers.overlays) ctx.drawImage(layers.overlays, 0, 0);
    canvas.dvcz = info;
    return canvas;
  }

  async function preload(sceneOrId, opts = {}) {
    const manifest = opts.manifest || state.manifest;
    const scene = getScene(sceneOrId, manifest);
    const size = scene.canvas_size || manifest.canvas_size;
    const assetW = (scene.asset_size || [1600])[0];
    const scale = Math.min(1, (opts.width || opts.maxWidth || Math.min(1600, assetW)) / size[0]);
    await sceneLayers(scene, Math.round(size[0] * scale), Math.round(size[1] * scale));
    const designs = new Set();
    [scene, ...(scene.layouts || [])].forEach((v) => (v.design_layers || []).forEach((d) => designs.add(d.path)));
    await Promise.all([...designs].map(designImage));
  }

  /** Downloads a scene's layer files into the HTTP cache without scaling them
   *  into the layer cache: for warm-ups (set card hover / focus) made before
   *  the output size is known, so no LRU slot is spent on an unused size.
   *  opts.width: expected output width (picks the small set up to 800 px). */
  function prefetch(sceneOrId, opts = {}) {
    const manifest = opts.manifest || state.manifest;
    const scene = getScene(sceneOrId, manifest);
    const files = sceneFiles(scene, useSmall(scene, opts.width || Infinity));
    const urls = new Set([files.base, ...files.overlays, ...files.masks.values()]);
    return Promise.all([...urls].map((url) => loadImage(url).then(() => true)));
  }

  /** Downscale an uploaded photo once for previews (the original stays for print). */
  async function preparePhoto(source, maxSide = 2400) {
    let image = source;
    let revoke = null;
    if (typeof Blob !== 'undefined' && source instanceof Blob) {
      revoke = URL.createObjectURL(source);
      image = await loadImage(revoke);
    } else if (typeof source === 'string') {
      image = await loadImage(source);
    }
    try {
      const [w, h] = dimensions(image);
      const s = Math.min(1, maxSide / w, maxSide / h);
      const canvas = makeCanvas(Math.max(1, Math.round(w * s)), Math.max(1, Math.round(h * s)));
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      canvas.dvczSource = { width: w, height: h };
      return canvas;
    } finally {
      if (revoke) URL.revokeObjectURL(revoke);
    }
  }

  function drawGuides(canvas, scene) {
    const ctx = canvas.getContext('2d'), scale = canvas.width / scene.canvas_size[0];
    ctx.save();
    ctx.lineWidth = 2;
    ctx.font = '600 15px system-ui';
    const texts = Array.isArray(scene.text_slots)
      ? scene.text_slots
      : Object.entries(scene.text_slots || {}).map(([id, value]) => ({ id, quad: Array.isArray(value) ? value : value.quad }));
    const slots = [...(scene.slots || []), ...texts].filter((s) => Array.isArray(s.quad));
    slots.forEach((slot, i) => {
      ctx.strokeStyle = ['#20a19e', '#e98649', '#5861e9'][i % 3];
      ctx.fillStyle = ctx.strokeStyle;
      ctx.beginPath();
      slot.quad.forEach(([x, y], j) => (j ? ctx.lineTo(x * scale, y * scale) : ctx.moveTo(x * scale, y * scale)));
      ctx.closePath();
      ctx.stroke();
      slot.quad.forEach(([x, y], j) => {
        ctx.beginPath();
        ctx.arc(x * scale, y * scale, 4, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillText(['TL', 'TR', 'BR', 'BL'][j], x * scale + 8, y * scale - 8);
      });
    });
    ctx.restore();
  }

  window.DVCZ = {
    version: '1.0.0',
    get manifest() { return state.manifest; },
    loadManifest,
    getScene,
    sceneFor,
    layoutFor,
    resolveLayout,
    renderScene,
    preload,
    prefetch,
    preparePhoto,
    fitText,
    ensureEmoji, // owner 22.10: Noto emoji pictures (same as DAILE Work prints)
    drawGuides,
    clearCache() { sceneCache.clear(); designCache.clear(); },
    setCacheLimit(n) { state.cacheLimit = Math.max(1, n | 0); },
    // low level, reference names
    assertQuad,
    homography,
    project,
    cropCover,
    drawProjective,
    luminanceMask,
    applyPaperLighting,
    messageTexture,
    loadImage,
  };
})();
